document.addEventListener('alpine:init', () => {
    Alpine.data('tiersBar', (config = {}) => ({
        /* --- Config (passed in from Liquid) --------------------------------- */
        threshold: Number(config.threshold) || 100,           // Free shipping threshold in major units (e.g. 100 = 100.00)
        tier1Position: Number(config.tier1Position) || 50,    // Free Shipping marker position on bar (%)
        tier2Position: Number(config.tier2Position) || 100,   // 3 Free Gifts marker position on bar (%)
        moneyFormat: config.moneyFormat || '${{amount}}',     // Shopify money_format
        tier2GoalTextTemplate: config.tier2GoalText || '[count] Free Gifts',
        giftCountMonthly: Number(config.giftCountMonthly) || 4,
        giftCountQuarterly: Number(config.giftCountQuarterly) || 5,
        messages: {
            initial: config.initialMessage || 'You are [amount] away from Free Shipping',
            tier2Otp: config.tier2OtpMessage || 'Free Shipping unlocked. Subscribe and get [count] free gifts',
            tier2Sub: config.tier2SubMessage || 'You have unlocked [count] free gifts',
        },

        /* --- Derived state from the cart store ------------------------------ */

        /**
         * Cart total in cents (from the Shopify cart object).
         * @returns {number}
         */
        get cartTotal() {
            return this.$store.cart?.data?.total_price || 0;
        },

        /**
         * Free shipping threshold in cents.
         * @returns {number}
         */
        get thresholdCents() {
            return this.threshold * 100;
        },

        /**
         * Non-gift cart lines with an active selling plan.
         * @returns {Array}
         */
        get subscriptionLines() {
            const items = this.$store.cart?.items || [];
            return items.filter((item) =>
                item.selling_plan_allocation &&
                item.properties?._isFreeGift !== 'true'
            );
        },

        /**
         * True if any non-gift line item is a subscription.
         * @returns {boolean}
         */
        get hasSubProduct() {
            return this.subscriptionLines.length > 0;
        },

        /**
         * True if any subscription line is quarterly (90-day / quarterly product).
         * @returns {boolean}
         */
        get hasQuarterlySub() {
            return this.subscriptionLines.some((item) => {
                const handle = (item.handle || '').toLowerCase();
                const plan = item.selling_plan_allocation?.selling_plan;
                const interval = Number(plan?.interval_count) || 0;
                const planName = (plan?.name || '').toLowerCase();
                return (
                    handle.includes('quarterly') ||
                    interval >= 3 ||
                    item.properties?.upgraded_in_cart_quarterly === 'true' ||
                    /90/.test(planName)
                );
            });
        },

        /**
         * Gift lines currently in the cart (added by the cart GWP rules or a page).
         * @returns {number}
         */
        get giftLinesInCart() {
            const items = this.$store.cart?.items || [];
            return items.filter((item) => item.properties?._isFreeGift === 'true').length;
        },

        /**
         * Gift count for the [count] placeholder. With a subscription in the cart
         * the gift lines actually in the cart are counted, since the GWP rules
         * decide 4 or 5 per product (the Starter Kit at 50% includes the shaker);
         * the monthly/quarterly settings are the fallback until those lines land.
         * One-time carts show the monthly count as the promise.
         * @returns {number}
         */
        get giftCount() {
            if (!this.hasSubProduct) return this.giftCountMonthly;
            if (this.giftLinesInCart > 0) return this.giftLinesInCart;
            return this.hasQuarterlySub ? this.giftCountQuarterly : this.giftCountMonthly;
        },

        /**
         * Free Shipping tier reached: cart >= threshold OR a subscription is present.
         * @returns {boolean}
         */
        get isFreeShippingReached() {
            return this.hasSubProduct || this.cartTotal >= this.thresholdCents;
        },

        /**
         * 3 Free Gifts tier reached: requires a subscription product.
         * @returns {boolean}
         */
        get isGiftsReached() {
            return this.hasSubProduct;
        },

        /**
         * Amount still needed (in cents) to reach the Free Shipping threshold.
         * @returns {number}
         */
        get amountRemaining() {
            return Math.max(0, this.thresholdCents - this.cartTotal);
        },

        /**
         * True when initial message should be displayed.
         * @returns {boolean}
         */
        get isInitialState() {
            return !this.isFreeShippingReached;
        },

        /**
         * Supported amount placeholder token in initial message.
         * @returns {string}
         */
        get amountPlaceholderToken() {
            if (this.messages.initial.includes('[amount remains]')) return '[amount remains]';
            return '[amount]';
        },

        /**
         * Whether the initial message contains a supported amount placeholder.
         * @returns {boolean}
         */
        get hasAmountPlaceholder() {
            return this.messages.initial.includes('[amount]') || this.messages.initial.includes('[amount remains]');
        },

        /**
         * Initial message split around the amount placeholder.
         * @returns {{before: string, after: string}}
         */
        get initialMessageParts() {
            if (!this.hasAmountPlaceholder) {
                return { before: this.messages.initial, after: '' };
            }

            const token = this.amountPlaceholderToken;
            const parts = this.messages.initial.split(token);

            return {
                before: parts[0] || '',
                after: parts.slice(1).join(token) || '',
            };
        },

        /**
         * Replaces [count] in a template with the current gift count.
         * @param {string} template
         * @returns {string}
         */
        formatWithCount(template) {
            if (!template || !template.includes('[count]')) return template;
            return template.replace(/\[count\]/g, String(this.giftCount));
        },

        /**
         * Tier 2 goal marker label with [count] resolved.
         * @returns {string}
         */
        get tier2GoalText() {
            return this.formatWithCount(this.tier2GoalTextTemplate);
        },

        /**
         * Active message based on cart state.
         * @returns {string}
         */
        get message() {
            if (this.isGiftsReached) return this.formatWithCount(this.messages.tier2Sub);
            if (this.isFreeShippingReached) return this.formatWithCount(this.messages.tier2Otp);
            if (this.hasAmountPlaceholder) {
                return this.messages.initial.replace(this.amountPlaceholderToken, this.formatMoney(this.amountRemaining));
            }
            return this.messages.initial;
        },

        /**
         * Bar fill percentage based on cart state.
         *   - Sub product in cart            → tier2Position (100%)
         *   - OTP cart >= threshold          → tier1Position (stay on first dot)
         *   - OTP cart <  threshold          → proportional toward tier 1 marker
         * @returns {number}
         */
        get fillPercent() {
            if (this.isGiftsReached) return this.tier2Position;
            if (this.cartTotal >= this.thresholdCents) return this.tier1Position;

            const ratio = this.thresholdCents > 0 ? this.cartTotal / this.thresholdCents : 0;
            return Math.min(this.tier1Position, ratio * this.tier1Position);
        },

        /**
         * Formats a cent amount using Shopify's money formatter when available.
         * @param {number} cents
         * @returns {string}
         */
        formatMoney(cents) {
            if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
                return Shopify.formatMoney(cents, this.moneyFormat);
            }
            return (cents / 100).toFixed(2);
        },
    }));
});
